import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { domainsAPI, emailAPI, mailHealthAPI, webmailAPI } from '@hpanel/web-common/api/client'
import type {
  Domain,
  Mailbox,
  MailAlias,
  MailForwarder,
  MailServiceHealth,
} from '@hpanel/web-common/api/types'
import {
  emailAutorespondersAPI,
  type Autoresponder,
} from '@hpanel/web-common/api/email_autoresponders'
import { AutoresponderTab } from '../../components/client/Email/AutoresponderTab'
import { RelayInfoTab } from '../../components/client/Email/RelayInfoTab'
import {
  emailSpamAPI,
  type SpamRule,
} from '@hpanel/web-common/api/email_spam'
import { SpamFilterTab } from '../../components/client/Email/SpamFilterTab'
import { useAuth } from '@hpanel/web-common/contexts/AuthContext'

/**
 * Tenant Email Manager — single interface for every email surface a
 * tenant cares about: mailboxes, aliases, forwarders, autoresponders,
 * and spam controls. First mailbox per domain auto-seeds DKIM + SPF +
 * DMARC TXT records server-side.
 *
 * Prior to 2026-05-11 the sidebar's "Email" link pointed at a "Coming
 * Soon" placeholder while the working surface lived behind a hidden
 * `/client/email-v2` deep link. This file is the consolidated successor
 * — the placeholder is gone, both routes resolve here, and two
 * previously-unwired surfaces (Autoresponders, Spam) are now first-
 * class tabs.
 */

type Tab =
  | 'mailboxes'
  | 'aliases'
  | 'forwarders'
  | 'autoresponders'
  | 'spam'
  | 'relay'

const TAB_LABELS: Record<Tab, string> = {
  mailboxes: 'Mailboxes',
  aliases: 'Aliases',
  forwarders: 'Forwarders',
  autoresponders: 'Autoresponders',
  spam: 'Spam filters',
  relay: 'Relay',
}

export const EmailManager: React.FC = () => {
  const [tab, setTab] = useState<Tab>('mailboxes')
  const [domains, setDomains] = useState<Domain[]>([])
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [aliases, setAliases] = useState<MailAlias[]>([])
  const [forwarders, setForwarders] = useState<MailForwarder[]>([])
  const [autoresponders, setAutoresponders] = useState<Autoresponder[]>([])
  const [spamRules, setSpamRules] = useState<SpamRule[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const [webmail, setWebmail] = useState<{
    enabled: boolean
    public_url: string | null
  }>({ enabled: false, public_url: null })
  const [launchError, setLaunchError] = useState<string | null>(null)
  // null = unknown / probe failed: show no banner (a failed health probe must
  // never break the page or claim the service is down).
  const [mailHealth, setMailHealth] = useState<MailServiceHealth | null>(null)

  const [mbForm, setMbForm] = useState({
    domain_id: '',
    local_part: '',
    password: '',
    quota_mb: 1024,
  })
  const [aliasForm, setAliasForm] = useState({
    domain_id: '',
    source: '',
    destination: '',
  })
  const [fwdForm, setFwdForm] = useState({
    domain_id: '',
    source: '',
    external: '',
  })

  const { user } = useAuth()
  // Set by the change-password page when it navigates back here.
  const location = useLocation()
  const notice = (location.state as { notice?: string } | null)?.notice ?? null
  // Only what the server says is real: the mail host and the ports that
  // answered a connect from the panel. Unknown (health failed) = no hosts/ports.
  const mailHost = mailHealth?.mail_host ?? null
  const reachablePorts = new Set(
    (mailHealth?.ports ?? []).filter((p) => p.reachable).map((p) => p.port),
  )
  const incomingPorts = reachablePorts.has(993) ? ['993 with SSL/TLS'] : []
  const outgoingPorts = [
    ...(reachablePorts.has(587) ? ['587 with STARTTLS'] : []),
    ...(reachablePorts.has(465) ? ['465 with SSL/TLS'] : []),
  ]
  const mailAppsReady = incomingPorts.length > 0 || outgoingPorts.length > 0

  const refresh = async () => {
    setLoading(true)
    try {
      // Scope domains to the current user. /client/* pages always
      // present the user's own data — even when the logged-in account
      // is a superuser (admin viewing the tenant portal sees admin's
      // own domains, NOT the whole fleet). Backend /api/domains already
      // forces this filter for non-superusers; passing user.id closes
      // the gap for superusers in client context.
      const [d, mb, al, fw, ar, sr, wm] = await Promise.all([
        domainsAPI.list(user?.id),
        emailAPI.listMailboxes(),
        emailAPI.listAliases(),
        emailAPI.listForwarders(),
        emailAutorespondersAPI.list().catch(() => [] as Autoresponder[]),
        emailSpamAPI.listRules().catch(() => [] as SpamRule[]),
        webmailAPI.status().catch(() => ({ enabled: false, public_url: null })),
      ])
      setDomains(d.domains)
      setMailboxes(mb)
      setAliases(al)
      setForwarders(fw)
      setAutoresponders(ar)
      setSpamRules(sr)
      setWebmail(wm)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  const openWebmail = async (id: number) => {
    setLaunchError(null)
    try {
      const { url } = await webmailAPI.launch(id)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e: unknown) {
      console.error('[webmail] launch failed', e)
      setLaunchError(
        e instanceof Error ? e.message : 'Could not open webmail'
      )
    }
  }

  useEffect(() => {
    void refresh()
    mailHealthAPI
      .get()
      .then(setMailHealth)
      .catch((e: unknown) => {
        console.error('[email] health check failed', e)
        setMailHealth(null)
      })
  }, [])

  const domainName = (id: number) =>
    domains.find((d) => d.id === id)?.name ?? `domain #${id}`

  const createMailbox = async (e: React.FormEvent) => {
    e.preventDefault()
    if (mbForm.domain_id === '' || mbForm.password.length < 10) return
    try {
      await emailAPI.createMailbox({
        domain_id: Number(mbForm.domain_id),
        local_part: mbForm.local_part,
        password: mbForm.password,
        quota_mb: mbForm.quota_mb,
      })
      setMbForm({ domain_id: '', local_part: '', password: '', quota_mb: 1024 })
      await refresh()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Create failed')
    }
  }

  const deleteMailbox = async (id: number) => {
    if (!window.confirm('Delete this mailbox? Existing mail may be retained host-side.')) {
      return
    }
    try {
      await emailAPI.deleteMailbox(id)
      await refresh()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  const createAlias = async (e: React.FormEvent) => {
    e.preventDefault()
    if (aliasForm.domain_id === '') return
    try {
      await emailAPI.createAlias({
        domain_id: Number(aliasForm.domain_id),
        source: aliasForm.source,
        destination: aliasForm.destination,
      })
      setAliasForm({ domain_id: '', source: '', destination: '' })
      await refresh()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Create failed')
    }
  }

  const createForwarder = async (e: React.FormEvent) => {
    e.preventDefault()
    if (fwdForm.domain_id === '') return
    try {
      await emailAPI.createForwarder({
        domain_id: Number(fwdForm.domain_id),
        source: fwdForm.source,
        external: fwdForm.external,
      })
      setFwdForm({ domain_id: '', source: '', external: '' })
      await refresh()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Create failed')
    }
  }

  const mailboxAddress = (mb: Mailbox) =>
    `${mb.local_part}@${domainName(mb.domain_id)}`

  return (
    <div data-testid="email-manager-page" className="space-y-6">
      <div>
        <h1 data-testid="page-title" className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Email
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Create and manage email accounts for your domains. Set up forwarding
          rules, autoresponders, and spam filters — all in one place. The
          first mailbox per domain auto-publishes DKIM + SPF + DMARC TXT
          records.
        </p>
      </div>

      {notice && (
        <div
          data-testid="email-notice"
          role="status"
          className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-200 rounded p-3 text-sm"
        >
          {notice}
        </div>
      )}

      {mailHealth && !mailHealth.healthy && (
        <div
          data-testid="mail-health-banner"
          role="alert"
          className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 rounded p-3 text-sm"
        >
          {mailHealth.message ??
            'Email service is temporarily unavailable on this server.'}
        </div>
      )}

      {error && (
        <div
          data-testid="email-error"
          className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded p-3 text-sm"
        >
          {error}
        </div>
      )}

      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-6">
          {(['mailboxes', 'aliases', 'forwarders', 'autoresponders', 'spam', 'relay'] as const).map(
            (t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`email-tab-${t}`}
                className={`pb-2 px-1 border-b-2 text-sm font-medium ${
                  tab === t
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                {TAB_LABELS[t]}
              </button>
            ),
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-gray-600 dark:text-gray-400">Loading…</p>
      ) : tab === 'mailboxes' ? (
        <>
          <form
            onSubmit={createMailbox}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
          >
            <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
              Create mailbox
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
              <label htmlFor="mb-domain" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Domain
              </label>
              <select
                id="mb-domain"
                value={mbForm.domain_id}
                onChange={(e) =>
                  setMbForm({ ...mbForm, domain_id: e.target.value })
                }
                required
                className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
              >
                <option value="">— domain —</option>
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
              </div>
              <div>
              <label htmlFor="mb-local-part" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Address (the part before @)
              </label>
              <input
                id="mb-local-part"
                className="w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                placeholder="e.g. info"
                value={mbForm.local_part}
                onChange={(e) =>
                  setMbForm({ ...mbForm, local_part: e.target.value })
                }
                required
              />
              </div>
              <div>
              <label htmlFor="mb-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password (at least 10 characters)
              </label>
              <input
                id="mb-password"
                type="password"
                autoComplete="new-password"
                className="w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                value={mbForm.password}
                onChange={(e) =>
                  setMbForm({ ...mbForm, password: e.target.value })
                }
                minLength={10}
                required
              />
              </div>
              <div>
              <label htmlFor="mb-quota" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Quota (MB)
              </label>
              <input
                id="mb-quota"
                type="number"
                className="w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                value={mbForm.quota_mb}
                onChange={(e) =>
                  setMbForm({ ...mbForm, quota_mb: Number(e.target.value) })
                }
              />
              </div>
            </div>
            <button
              type="submit"
              className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
            >
              Create mailbox
            </button>
          </form>
          <div
            data-testid="mail-client-settings"
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300"
          >
            <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Setting up Outlook, Apple Mail or your phone
            </h2>
            {mailHost && mailAppsReady ? (
              <>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
                  <dt className="text-gray-500 dark:text-gray-400">Server</dt>
                  <dd className="font-mono" data-testid="mail-host">{mailHost}</dd>
                  {incomingPorts.length > 0 && (
                    <>
                      <dt className="text-gray-500 dark:text-gray-400">Incoming (IMAP)</dt>
                      <dd data-testid="mail-incoming">port {incomingPorts.join(', or ')}</dd>
                    </>
                  )}
                  {outgoingPorts.length > 0 && (
                    <>
                      <dt className="text-gray-500 dark:text-gray-400">Outgoing (SMTP)</dt>
                      <dd data-testid="mail-outgoing">port {outgoingPorts.join(', or ')}</dd>
                    </>
                  )}
                  <dt className="text-gray-500 dark:text-gray-400">Username</dt>
                  <dd>the full email address</dd>
                  <dt className="text-gray-500 dark:text-gray-400">Password</dt>
                  <dd>the mailbox password (not your panel password)</dd>
                </dl>
              </>
            ) : mailHealth ? (
              <p data-testid="mail-apps-unavailable">
                Mail apps (Outlook, Apple Mail, phones) can&apos;t connect to this
                server yet. Use webmail to read and send mail in the meantime.
              </p>
            ) : (
              <p data-testid="mail-apps-unknown">
                Mail server settings could not be loaded. Reload the page to try again.
              </p>
            )}
          </div>
          {!webmail.enabled && (
            <div
              data-testid="webmail-disabled-banner"
              className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-200"
            >
              Webmail isn&apos;t configured on this server yet. Ask your
              administrator to enable it; mailbox creation still works.
            </div>
          )}
          {launchError && (
            <div
              data-testid="webmail-launch-error"
              className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-4 text-sm text-red-800 dark:text-red-200"
            >
              {launchError}
            </div>
          )}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-left">
                <tr>
                  <th className="px-4 py-3">Address</th>
                  <th className="px-4 py-3">Quota</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Inbox</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {mailboxes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      No mailboxes yet.
                    </td>
                  </tr>
                ) : (
                  mailboxes.map((m) => (
                    <tr
                      key={m.id}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <td className="px-4 py-3 font-mono">
                        {mailboxAddress(m)}
                      </td>
                      <td className="px-4 py-3">{m.quota_mb} MB</td>
                      <td className="px-4 py-3">{m.status}</td>
                      <td className="px-4 py-3">
                        {webmail.enabled ? (
                          <button
                            data-testid={`open-webmail-${m.id}`}
                            onClick={() => openWebmail(m.id)}
                            className="text-primary-600 hover:underline"
                          >
                            Open
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                        <a
                          href={`/api/email/mailboxes/${m.id}/mobileconfig`}
                          download
                          data-testid={`mailbox-mobileconfig-${m.id}`}
                          title="Download a profile that adds this mailbox to an iPhone, iPad or Mac. It asks for the mailbox password when installed."
                          className="text-primary-600 hover:underline"
                        >
                          Set up on iPhone/Mac
                        </a>
                        <Link
                          to={`/client/email/mailboxes/${m.id}/password`}
                          data-testid={`mailbox-password-${m.id}`}
                          className="text-primary-600 hover:underline"
                        >
                          Change password
                        </Link>
                        <button
                          onClick={() => deleteMailbox(m.id)}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : tab === 'aliases' ? (
        <>
          <form
            onSubmit={createAlias}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
          >
            <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
              Create alias
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select
                value={aliasForm.domain_id}
                onChange={(e) =>
                  setAliasForm({ ...aliasForm, domain_id: e.target.value })
                }
                required
                className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
              >
                <option value="">— domain —</option>
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
              <input
                className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                placeholder="source (e.g. sales)"
                value={aliasForm.source}
                onChange={(e) =>
                  setAliasForm({ ...aliasForm, source: e.target.value })
                }
                required
              />
              <input
                className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                placeholder="destination (comma-separated)"
                value={aliasForm.destination}
                onChange={(e) =>
                  setAliasForm({ ...aliasForm, destination: e.target.value })
                }
                required
              />
            </div>
            <button
              type="submit"
              className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
            >
              Create alias
            </button>
          </form>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-left">
                <tr>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Destination</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {aliases.map((a) => (
                  <tr
                    key={a.id}
                    className="border-t border-gray-200 dark:border-gray-700"
                  >
                    <td className="px-4 py-3 font-mono">
                      {a.source}@{domainName(a.domain_id)}
                    </td>
                    <td className="px-4 py-3 break-all">{a.destination}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={async () => {
                          await emailAPI.deleteAlias(a.id)
                          await refresh()
                        }}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : tab === 'forwarders' ? (
        <>
          <form
            onSubmit={createForwarder}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
          >
            <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
              Create forwarder
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select
                value={fwdForm.domain_id}
                onChange={(e) =>
                  setFwdForm({ ...fwdForm, domain_id: e.target.value })
                }
                required
                className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
              >
                <option value="">— domain —</option>
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
              <input
                className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                placeholder="source (local part)"
                value={fwdForm.source}
                onChange={(e) =>
                  setFwdForm({ ...fwdForm, source: e.target.value })
                }
                required
              />
              <input
                type="email"
                className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                placeholder="external (full email)"
                value={fwdForm.external}
                onChange={(e) =>
                  setFwdForm({ ...fwdForm, external: e.target.value })
                }
                required
              />
            </div>
            <button
              type="submit"
              className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
            >
              Create forwarder
            </button>
          </form>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-left">
                <tr>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">External</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {forwarders.map((f) => (
                  <tr
                    key={f.id}
                    className="border-t border-gray-200 dark:border-gray-700"
                  >
                    <td className="px-4 py-3 font-mono">
                      {f.source}@{domainName(f.domain_id)}
                    </td>
                    <td className="px-4 py-3 font-mono break-all">{f.external}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={async () => {
                          await emailAPI.deleteForwarder(f.id)
                          await refresh()
                        }}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : tab === 'autoresponders' ? (
        <AutoresponderTab
          mailboxes={mailboxes}
          autoresponders={autoresponders}
          mailboxAddress={mailboxAddress}
          onChange={refresh}
          onError={setError}
        />
      ) : tab === 'spam' ? (
        <SpamFilterTab
          mailboxes={mailboxes}
          rules={spamRules}
          mailboxAddress={mailboxAddress}
          onChange={refresh}
          onError={setError}
        />
      ) : (
        <RelayInfoTab />
      )}
    </div>
  )
}
